import { toastError, toastWarning } from '@components/Toast';

import { TNA_REQUEST_TYPE } from '@features/order/utils/projectUtil';

import ApiClient from '@lib/apiClient';

import { downloadBlobFIle, printApiError } from '@utils/Util';

const getAllTnaProductList = async (orderId) => {
	try {
		const { data } = await ApiClient.GET('getInitialAllOrder', '/' + orderId);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const getTemplateDays = async () => {
	try {
		const { data } = await ApiClient.GET('getTnaTemplateDay');
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const selectedTna = async (
	orderId,
	rfqId,
	templateName = '',
	idOfSetMember = null,
	planningSource = TNA_REQUEST_TYPE.EXCEL_FILE
) => {
	try {
		let url = '/' + orderId + '/rfq/' + rfqId;

		if (templateName !== '' && !idOfSetMember) {
			url += `?templateName=${templateName}&planningSource=${planningSource}`;
		} else if (!idOfSetMember && !templateName) {
			url += `?planningSource=${planningSource}`;
		} else if (idOfSetMember && !templateName) {
			url += `?setMemberId=${idOfSetMember}&planningSource=${planningSource}`;
		} else if (templateName !== '' && idOfSetMember) {
			url += `?templateName=${templateName}&setMemberId=${idOfSetMember}&planningSource=${planningSource}`;
		}

		const { data } = await ApiClient.GET('tnaPlanningOrder', url);

		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const updateDuration = async (orderId, rfqId, reqBody, idOfSetMember = null) => {
	try {
		const { data } = idOfSetMember
			? await ApiClient.PUT(
					'tnaPlanningOrder',
					reqBody,
					`/${orderId}/rfq/${rfqId}/update-duration?setMemberId=${idOfSetMember}`
				)
			: await ApiClient.PUT('tnaPlanningOrder', reqBody, `/${orderId}/rfq/${rfqId}/update-duration`);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};
const updateDeliveryDate = async (orderId, rfqId, reqBody, idOfSetMember = null) => {
	try {
		const { data } = idOfSetMember
			? await ApiClient.PUT(
					'tnaPlanningOrder',
					reqBody,
					`/${orderId}/rfq/${rfqId}/update-delivery-date?setMemberId=${idOfSetMember}`
				)
			: await ApiClient.PUT(
					'tnaPlanningOrder',
					reqBody,
					`/${orderId}/rfq/${rfqId}/update-delivery-date`
				);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const updateNotApplicable = async (orderId, rfqId, reqBody, idOfSetMember = null) => {
	try {
		const { data } = idOfSetMember
			? await ApiClient.PUT(
					'tnaPlanningOrder',
					reqBody,
					`/${orderId}/rfq/${rfqId}/update-not-applicable?setMemberId=${idOfSetMember}`
				)
			: await ApiClient.PUT(
					'tnaPlanningOrder',
					reqBody,
					`/${orderId}/rfq/${rfqId}/update-not-applicable`
				);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const updateTask = async (orderId, rfqId, reqBody, idOfSetMember = null) => {
	try {
		const { data } = idOfSetMember
			? await ApiClient.POST(
					'tnaPlanningOrder',
					reqBody,
					`/${orderId}/rfq/${rfqId}/add-task?setMemberId=${idOfSetMember}`
				)
			: await ApiClient.POST('tnaPlanningOrder', reqBody, `/${orderId}/rfq/${rfqId}/add-task`);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

// The source style can sit on any order, so its order is sent along with it; withDueDates says
// whether its dates come across with its tasks.
const duplicateTask = async (
	orderId,
	rfqId,
	plannedRfqId,
	productId,
	plannedProductId,
	runningProdId,
	plannedOrderId,
	withDueDates = true
) => {
	try {
		const { data } = await ApiClient.POST(
			'tnaPlanningOrder',
			'',
			`/${orderId}/rfq/${rfqId}/duplicate/${plannedRfqId}?productId=${
				productId ? productId : runningProdId
			}&plannedProductId=${plannedProductId}${
				plannedOrderId ? `&plannedOrderId=${plannedOrderId}` : ''
			}&withDueDates=${withDueDates}`
		);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const submitTna = async (orderId, rfqId, idOfSetMember = null, taskDueDates = []) => {
	try {
		const { data } = idOfSetMember
			? await ApiClient.POST(
					'submitTna',
					taskDueDates,
					`/${orderId}/rfq/${rfqId}/complete?setMemberId=${idOfSetMember}`
				)
			: await ApiClient.POST('submitTna', taskDueDates, `/${orderId}/rfq/${rfqId}/complete`);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const submitIndividualTna = async (orderId, rfqId, query) => {
	try {
		const params = `/${orderId}/rfq/${rfqId}/create-tna-individual${query ? query : ''}`;

		const { data } = await ApiClient.POST('submitRunningTna', '', params);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const getAllStylePlanning = async (orderId) => {
	try {
		const { data } = await ApiClient.GET('getRunningAllOrder', '/' + orderId);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const getRunningTnaStyleDetails = async (orderId, rfqId, idOfSetMember) => {
	try {
		const { data } = idOfSetMember
			? await ApiClient.GET(
					'getRunningTnaStyleDetails',
					`/${orderId}/rfq/${rfqId}?setMemberId=${idOfSetMember}`
				)
			: await ApiClient.GET('getRunningTnaStyleDetails', `/${orderId}/rfq/${rfqId}`);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const subtaskCreate = async (stepId, body) => {
	try {
		const { data } = await ApiClient.POST('stepUrl', body, `/${stepId}/add-sub-task`);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const subtaskUpdate = async (subTaskId, body) => {
	try {
		const { data } = await ApiClient.PUT('subTaskAction', body, subTaskId);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const qtyUpdate = async (stepId, quantity) => {
	try {
		const { data } = await ApiClient.PUT(
			'stepUrl',
			'',
			`/${stepId}/production-quantity?quantity=${quantity}`
		);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const taskEndDateUpdate = async (body, taskId) => {
	try {
		const { data } = await ApiClient.PUT('task', body, taskId);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const getChannels = async (stepId) => {
	try {
		const { data } = await ApiClient.GET('stepUrl', `/${stepId}`);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const onSubmitPoQuantity = async (id, quantity) => {
	try {
		const { data } = await ApiClient.PUT('stepUrl', '', `/${id}/production-target?quantity=${quantity}`);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const onDeletePoQuantity = async (stepId, quantityId) => {
	try {
		const { data } = await ApiClient.DELETE(
			'stepUrl',
			'',
			`/${stepId}/production-quantity/${quantityId}`
		);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const onDleteTask = async (id) => {
	try {
		const { data } = await ApiClient.DELETE('subTaskAction', {}, id);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const fetchPostComments = async (paramsData) => {
	let params = `${paramsData.orderId}/product/${paramsData.ProductId}?page=${
		paramsData.page
	}&size=${10}&sort=id,desc`;
	if (paramsData?.deliverableId) {
		params += `&deliverableId=${paramsData.deliverableId}`;
	}
	try {
		const { data = [] } = await ApiClient.GET('orderTimelineAction', params);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const onApproveTask = async (taskId) => {
	try {
		const { data } = await ApiClient.POST('approveTask', {}, taskId);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastWarning(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const getOrderDashboardInfoByOrderId = async (orderId) => {
	try {
		const { data } = await ApiClient.GET(
			'getOrderDashboardReports',
			`?orderIdList=${orderId}&page=0&size=1000&sort=id,desc`
		);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const getGroupWiseOrderCount = async (params = '') => {
	try {
		const { data } = await ApiClient.GET('getOrderDashboardGroupWiseReports', params);
		return data;
	} catch (error) {
		printApiError(error);
	}
};

const onGetDeliverableDetails = async (deliverableId) => {
	try {
		const { data } = await ApiClient.GET('getDeliverableDetails', deliverableId);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Something went wrong! Please try again.');
		}
	}
};

const downloadTnaExcelFile = async (orderId, rfqIds) => {
	try {
		const query = `?rfqIds=${rfqIds}`;
		const response = await ApiClient.DOWNLOAD_GET('downloadTnaExcelFile', orderId, query);
		downloadBlobFIle(response);
		return response;
	} catch (error) {
		printApiError(error);
	}
};

const uploadTnaExcelFile = async (files, orderId) => {
	try {
		const data = await ApiClient.UPLOAD_MULTIPLE_FILE('uploadTnaExcelFile', files, orderId);
		return data;
	} catch (error) {
		printApiError(error);
	}
};

// The caller decides how to report a failure, so the error is left to propagate.
const onStartOrder = async (orderId) => {
	const { data } = await ApiClient.POST('startOrder', '', `/${orderId}/create-tna`);
	return data;
};

// The finished plans that can be copied onto a style, found by style number, SKU number or order.
const searchDuplicablePlans = async (search) => {
	try {
		const { data } = await ApiClient.GET(
			'searchDuplicablePlans',
			`?search=${encodeURIComponent(search)}`
		);
		return data;
	} catch ({ response }) {
		if (response && response.data && response.data.message) {
			toastError(response.data.message);
		} else {
			toastError('Could not search for styles to duplicate from.');
		}
		return [];
	}
};

export default {
	getAllTnaProductList,
	getTemplateDays,
	selectedTna,
	updateDuration,
	updateDeliveryDate,
	updateNotApplicable,
	updateTask,
	duplicateTask,
	searchDuplicablePlans,
	submitTna,
	getAllStylePlanning,
	getRunningTnaStyleDetails,
	subtaskCreate,
	subtaskUpdate,
	qtyUpdate,
	taskEndDateUpdate,
	getChannels,
	onSubmitPoQuantity,
	onDeletePoQuantity,
	onDleteTask,
	fetchPostComments,
	onApproveTask,
	getOrderDashboardInfoByOrderId,
	onGetDeliverableDetails,
	submitIndividualTna,
	getGroupWiseOrderCount,
	downloadTnaExcelFile,
	uploadTnaExcelFile,
	onStartOrder
};
